/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useMemo, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'

// Components
import {
    AccordionItem,
    AccordionButton,
    AccordionIcon,
    AccordionPanel,
    Box,
    HStack,
    Text,
    Flex,
    Image,
    Link as ChakraLink,
    Link,
    Button,
    List,
    ListItem
} from '@chakra-ui/react'

// Hooks
import MapPage from './google-maps'
import useBreakpoints from '../../hooks/use-breakpoints'

const StoresList = ({storesInfo, onLoadMoreClicked, isLoadMoreButtonShowed}) => {
    const intl = useIntl()
    const [activeStore, setActiveStore] = useState(null)
    const isMobile = useBreakpoints({base: true, lg: false})

    const mappedStores = useMemo(() => {
        return (
            storesInfo?.map((store) => ({
                id: store.id,
                name: store.name,
                position: {
                    lat: store.latitude,
                    lng: store.longitude
                },
                infoWindowHtml: store.infoWindowHtml
            })) ?? []
        )
    }, [storesInfo])

    return (
        <Flex flexWrap={{base: 'wrap', lg: 'nowrap'}} flexDirection={{base: 'column', lg: 'row'}}>
            {!!storesInfo && !isMobile && (
                <Box width="50%" minWidth="50%">
                    <MapPage locations={mappedStores} activeMarker={activeStore} dontFocusMarker />
                </Box>
            )}
            <Box width={{base: '100%', lg: '50%'}} minWidth={{base: '100%', lg: '50%'}}>
                <List width="100%" overflow="auto" maxHeight="500px" pl={{base: 0, lg: 4}}>
                    {storesInfo?.map((store, index) => {
                        const headingId = index + '-' + store.name.replace(/\s+/g, '')
                        return (
                            <ListItem key={index}>
                                <AccordionItem
                                    borderTop="1px solid"
                                    borderColor={index > 0 ? 'inherit' : 'transparent'}
                                    data-testid="store-element"
                                    aria-describedby={headingId}
                                >
                                    <HStack
                                        align="flex-start"
                                        mt={4}
                                        mb={4}
                                        maxW="calc(100% - 20px)"
                                        aria-label={intl.formatMessage({
                                            id: 'warranty.confirmation.store',
                                            defaultMessage: 'Store'
                                        })}
                                        role="group"
                                        onMouseEnter={() => {
                                            setActiveStore({
                                                position: {
                                                    lat: Number(store.latitude),
                                                    lng: Number(store.longitude)
                                                },
                                                infoWindowHtml: store.infoWindowHtml,
                                                id: store.id
                                            })
                                        }}
                                        onMouseLeave={() => {
                                            setActiveStore(null)
                                        }}
                                    >
                                        <Text textStyle="Regular XLarge">{index + 1}</Text>
                                        <Flex
                                            id={`store-info-${store.id}`}
                                            width="100%"
                                            flexDirection="column"
                                            gap={2}
                                            position="relative"
                                        >
                                            <Flex
                                                flexDirection={{base: 'column', md: 'row'}}
                                                justifyContent="space-between"
                                            >
                                                {store.name && (
                                                    <Text
                                                        as="h2"
                                                        textStyle="Regular XLarge"
                                                        isTruncated
                                                        id={headingId}
                                                    >
                                                        {store.name}
                                                    </Text>
                                                )}
                                                {store['c_store-logo'] && (
                                                    <Image
                                                        src={store['c_store-logo']}
                                                        height="50px"
                                                        paddingTop={{base: 2, md: 0}}
                                                        width="auto"
                                                        objectFit="contain"
                                                        alignSelf="start"
                                                        alt=""
                                                    />
                                                )}
                                            </Flex>
                                            <Box
                                                textStyle="Regular Medium"
                                                color="stokkeCore.accessibleGrey"
                                            >
                                                {store.address1}
                                            </Box>
                                            <Box
                                                textStyle="Regular Medium"
                                                color="stokkeCore.accessibleGrey"
                                            >
                                                <span>{store.city}</span>
                                                <span>,&nbsp;</span>
                                                <span>
                                                    {store.stateCode ? store.stateCode : ''}
                                                </span>{' '}
                                                <span>{store.postalCode}</span>
                                            </Box>
                                            {store.phone && (
                                                <Box
                                                    textStyle="Regular Medium"
                                                    color="stokkeCore.accessibleGrey"
                                                >
                                                    {intl.formatMessage({
                                                        id: 'store_locator.description.phone',
                                                        defaultMessage: 'Phone:'
                                                    })}{' '}
                                                    <Link
                                                        href={`tel:${store.phone}`}
                                                        color="stokkeCore.accessibleGrey"
                                                    >
                                                        {store.phone}
                                                    </Link>
                                                </Box>
                                            )}
                                            {store.c_website && (
                                                <Text
                                                    color="stokkeCore.accessibleGrey"
                                                    isTruncated
                                                    maxW="100%"
                                                    whiteSpace="nowrap"
                                                    overflow="hidden"
                                                    textOverflow="ellipsis"
                                                >
                                                    {intl.formatMessage({
                                                        id: 'store_locator.website',
                                                        defaultMessage: 'Website:'
                                                    })}
                                                    <span> </span>
                                                    <ChakraLink
                                                        color="stokkeCore.accessibleGrey"
                                                        href={store.c_website}
                                                        size="md"
                                                        isExternal
                                                        variant="underlineLink"
                                                    >
                                                        {store.c_website}
                                                    </ChakraLink>
                                                </Text>
                                            )}
                                            <Flex gap={2} flexWrap="wrap">
                                                {store.distance !== undefined && (
                                                    <Flex layerStyle="chip">
                                                        {store.distance + ' ' + store.distanceUnit}
                                                    </Flex>
                                                )}
                                                {store['c_full-partner'] && (
                                                    <Flex layerStyle="chip">
                                                        <FormattedMessage
                                                            defaultMessage="Full assortment"
                                                            id="store_locator.full_partner"
                                                        />
                                                    </Flex>
                                                )}
                                                {store['c_destination-partner'] && (
                                                    <Flex layerStyle="chip">
                                                        <FormattedMessage
                                                            defaultMessage="Destination store"
                                                            id="store_locator.destination_store"
                                                        />
                                                    </Flex>
                                                )}
                                            </Flex>
                                            {(store.storeHours || store.c_sells) && (
                                                <>
                                                    {' '}
                                                    <AccordionButton
                                                        sx={{
                                                            marginTop: '10px',
                                                            paddingBottom: '0px'
                                                        }}
                                                    >
                                                        <Text textStyle="Regular XLarge">
                                                            {intl.formatMessage({
                                                                id: 'store_locator.action.viewMore',
                                                                defaultMessage: 'View More'
                                                            })}
                                                        </Text>
                                                        <AccordionIcon />
                                                    </AccordionButton>
                                                    <AccordionPanel mb={6} mt={4}>
                                                        {store?.storeHours && (
                                                            <Box
                                                                color="stokkeCore.accessibleGrey"
                                                                dangerouslySetInnerHTML={{
                                                                    __html: store.storeHours
                                                                }}
                                                            />
                                                        )}
                                                        {store.c_sells && (
                                                            <>
                                                                <Text
                                                                    as="h3"
                                                                    textStyle="Regular Medium"
                                                                    color="stokkeCore.accessibleGrey"
                                                                    paddingY={2}
                                                                >
                                                                    {intl.formatMessage({
                                                                        id: 'store_locator.products',
                                                                        defaultMessage: 'products:'
                                                                    })}
                                                                </Text>
                                                                <Flex
                                                                    as="ul"
                                                                    gap={2}
                                                                    flexWrap="wrap"
                                                                >
                                                                    {store.c_sells?.map(
                                                                        (product) => (
                                                                            <Flex
                                                                                as="li"
                                                                                key={`${store.id}-${product}`}
                                                                                layerStyle="chip"
                                                                            >
                                                                                {product}
                                                                            </Flex>
                                                                        )
                                                                    )}
                                                                </Flex>
                                                            </>
                                                        )}
                                                    </AccordionPanel>{' '}
                                                </>
                                            )}
                                        </Flex>
                                    </HStack>
                                </AccordionItem>
                            </ListItem>
                        )
                    })}
                </List>
                {isLoadMoreButtonShowed && (
                    <Flex paddingTop="10px" marginTop="10px" justify="center">
                        <Button
                            key="load-more-button"
                            onClick={onLoadMoreClicked}
                            width="100%"
                            variant="outline"
                            marginBottom={4}
                        >
                            {intl.formatMessage({
                                id: 'global.loadmore',
                                defaultMessage: 'Load more'
                            })}
                        </Button>
                    </Flex>
                )}
            </Box>

            {!!storesInfo && isMobile && (
                <Box width="100%" minWidth="100%">
                    <MapPage locations={mappedStores} activeMarker={activeStore} />
                </Box>
            )}
        </Flex>
    )
}

StoresList.propTypes = {
    storesInfo: PropTypes.array,
    onLoadMoreClicked: PropTypes.func,
    isLoadMoreButtonShowed: PropTypes.bool
}

export default StoresList
